import React from 'react';

type Props = {
  children: React.ReactNode;
  onError?: (error: Error) => void;
  /** Test-only escape hatch: force the boundary into an error state without throwing. */
  forceError?: boolean;
};

type State = { hasError: boolean };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    if (this.props.forceError) {
      this.triggerForcedError();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.forceError && this.props.forceError) {
      this.triggerForcedError();
    }
  }

  private triggerForcedError() {
    const err = new Error('Forced error');
    this.setState({ hasError: true });
    try {
      this.props.onError?.(err);
    } catch {
      // ignore
    }
  }

  componentDidCatch(error: Error) {
    try {
      this.props.onError?.(error);
    } catch {
      // ignore
    }
  }

  render() {
    if (this.state.hasError) {
      // Render minimal fallback; toast is the user-facing surface.
      return (
        <div className="max-w-3xl mx-auto py-10 px-6">
          <h1 className="text-2xl font-semibold text-NBNA-primary">Something went wrong</h1>
          <p className="mt-2 text-gray-700">Please refresh and try again.</p>
        </div>
      );
    }

    return this.props.children;
  }
}
